import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { StorageService } from './storage.service';
import { AuthenticatedResult } from 'angular-auth-oidc-client';
import { Router } from '@angular/router';
import { Constants } from '../../constants';
import { PermissionType } from '../models/enum/permission.enum';
import { PermissionsService } from './permissions.service';

@Injectable({
  providedIn: 'root'
})
export class RoutingService {

  private adminPages = [Constants.customersPanelPage, Constants.usersPanelPage];

  constructor(
    private storage: StorageService,
    private auth: AuthService,
    private permissionsService: PermissionsService,
    private router: Router
  ) { }

  isLoggedInRouting() {
    this.auth.isLoggedIn().subscribe(async (resp: AuthenticatedResult) => {
      if (resp.isAuthenticated && !this.storage.getLogoutState()) {
        if (this.router.url !== Constants.loginPage) {
          return;
        }
        await this.handleAuthentication();
      } else {
        await this.router.navigate([Constants.loginPage]);
      }
    });
  }

  private async handleAuthentication() {
    const is2FAEnabled = await this.auth.isTwoFactorAuthenticationEnabled();
    if (!is2FAEnabled.twoFactorAuthenticationEnabled) {
      await this.router.navigate([Constants.qrAuthPage]);
    } else {
      await this.navigateByRoles();
    }
  }

  public stripParametersFromLastViewedPage(pageUrl?: string) {
    if (pageUrl) {
      if (pageUrl.match('organizationId')) {
        // Keep organizationId but throw everything else away
        return pageUrl?.split('&')[0];
      } else {
        // Remove everything
        return pageUrl?.split('?')[0];
      }
    }
    return pageUrl;
  }

  async navigateByRoles() {
    const profile = await this.auth.getUserProfile();
    const lastViewedPageWithParams = this.storage.getLastViewedPage();
    const lastViewedPage = this.stripParametersFromLastViewedPage(lastViewedPageWithParams);
    let page = Constants.analysisRequestPage;
    if (lastViewedPage) {
      const url = new URL(lastViewedPage, window.location.origin);
      page = url.pathname.replace('/', '');
    }
    const hasAccess = await this.permissionsService.hasAccess(page);
    const navigateUrl = lastViewedPage ? lastViewedPage : Constants.analysisRequestPage;

    if (hasAccess) {
      await this.navigateToPage(navigateUrl, profile.permissions.map(p => p.name));
    } else {
      await this.router.navigate([Constants.analysisRequestPage]);
    }
  }

  private async navigateToPage(page: string, permissions: PermissionType[]) {
    const url = new URL(page, window.location.origin);
    let queryParams = Object.fromEntries(url.searchParams.entries());

    if (url.pathname === Constants.analysisRequestPage) {
      const allowedPermissions = [PermissionType.portalAdmin, PermissionType.portalUser,];
      if (!permissions.some(permission => allowedPermissions.includes(permission))) {
        const filteredQueryParams = Array.from(url.searchParams).filter(([key]) => key.toLowerCase() !== Constants.OrganizationIdParam);
        queryParams = Object.fromEntries(filteredQueryParams);
      }
    }

    await this.router.navigate([url.pathname], { queryParams });
  }

  isAdminPage(page: string): boolean {
    const url = new URL(page, window.location.origin);
    return this.adminPages.includes(url.pathname);
  }
}
